import { AutocompleteWithLabel, SelectWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useQuery
} from '@apollo/client'
import { Button, Grow, MenuItem } from '@material-ui/core'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { PMS_TYPE_NAMES_QUERY } from '../../graphql/pmstypes'
import { errorToastMessage } from '../../helpers/errorToast'
import { getTranslate, lang } from '../../localization'
import { PMSTypeName, ProviderAdapter } from '../../types/accommodation'
import { CRSStatus } from '../../types/booking'
import { EntityList } from '../../types/entityList'
import { ReserveMonitoringListResponse } from '../../types/reserveMonitoringList'
import { CRSStatusOptions } from '../BookingList/BookingListFilter/CRSAndPMSStatusOptions'
import useStyles from './styles/MonitoringReserveFilters.styles'

type MonitoringReserveFiltersProps = {
  autoCompleteForAccommodation: any[]
  setAutoCompleteForAccommodation: React.Dispatch<React.SetStateAction<any[]>>
  getReserveMonitoringList: LazyQueryExecFunction<
    {
      reserveMonitoringList: ReserveMonitoringListResponse
    },
    OperationVariables
  >
  loadingForReserveMonitoringList: boolean
  accommodations: EntityList[]
  disabledAccs: boolean
  setDisabledAccs: React.Dispatch<React.SetStateAction<boolean>>
}

export default function MonitoringReserveFilters({
  autoCompleteForAccommodation,
  setAutoCompleteForAccommodation,
  getReserveMonitoringList,
  loadingForReserveMonitoringList,
  accommodations,
  disabledAccs,
  setDisabledAccs
}: MonitoringReserveFiltersProps) {
  const classes = useStyles()
  const [providerAdapter, setProviderAdapter] = useState<ProviderAdapter>('')
  const [crsStatus, setCRSStatus] = useState<CRSStatus>('Confirmed')
  const [pmsTypes, setPMSTypes] = useState<PMSTypeName[]>([])

  // A failure here left pmsTypes as [] and the adapter filter simply rendered
  // empty, with nothing said. That is how a 403 -- the resolver had no IAM
  // endpoint-permission mapping, and Layer 1 fails closed on an unmapped endpoint --
  // was reported and triaged as missing data rather than as an authorization error.
  // An empty list and a denied request must not look the same.
  //
  // Routed through errorToastMessage, not error.message: it maps FORBIDDEN to "You do
  // not have permission to perform this action" -- which is what this failure actually
  // was -- and keeps raw resolver text, which may carry identifiers, off the screen.
  const {} = useQuery<{
    pmsTypeNames: PMSTypeName[]
  }>(PMS_TYPE_NAMES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      setPMSTypes(res.pmsTypeNames)
    },
    onError: (error) => {
      toast.error(errorToastMessage(error))
    }
  })

  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.eachRow}>
          <SelectWithLabel
            noHelperText
            fullWidth
            value={providerAdapter}
            label={getTranslate('PMS Type')}
            size="small"
            onChange={(e) => {
              setProviderAdapter(e.target.value as ProviderAdapter)
              setAutoCompleteForAccommodation([])
              setDisabledAccs(true)
            }}>
            {pmsTypes.map((item, index) => (
              <MenuItem key={index} value={item.EN}>
                {lang === 'fa' ? item.FA : item.EN}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <AutocompleteWithLabel
            showSelectAll
            multiple
            noHelperText
            limitTags={1}
            disabled={disabledAccs}
            value={autoCompleteForAccommodation}
            fullWidth
            getOptionLabel={(option) => option.label || ''}
            label={getTranslate('Accommodation')}
            inputSize="small"
            size="small"
            options={
              accommodations
                ? accommodations.map((ele) => ({
                    label: ele.label,
                    value: ele.value
                  }))
                : []
            }
            onChange={(_, v: any) => {
              if (v.length > 0) {
                const tempSelectedAccommodations = [
                  ...v.map((item: any) => ({
                    label: item.label,
                    value: item.value
                  }))
                ]
                setAutoCompleteForAccommodation(tempSelectedAccommodations)
              } else {
                setAutoCompleteForAccommodation([])
              }
            }}
            loading={loadingForReserveMonitoringList}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={classes.eachRow}>
          <SelectWithLabel
            noHelperText
            fullWidth
            value={crsStatus}
            label={getTranslate('CRS Status')}
            size="small"
            onChange={(e) => {
              setCRSStatus(e.target.value as CRSStatus)
            }}>
            {CRSStatusOptions.map((item) => (
              <MenuItem key={item} value={item}>
                {getTranslate(item)}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <div className={classes.buttonContainers}>
            <Button
              variant="text"
              className={classes.resetButton}
              onClick={() => {
                setAutoCompleteForAccommodation([])
                setProviderAdapter('')
                setCRSStatus('Confirmed')
                setDisabledAccs(true)
              }}>
              {getTranslate('Reset')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={loadingForReserveMonitoringList || !providerAdapter}
              onClick={() => {
                getReserveMonitoringList({
                  variables: {
                    providerAdapter: providerAdapter,
                    crsStatus: crsStatus
                  }
                })
              }}>
              {getTranslate('Apply')}
            </Button>
          </div>
        </div>
      </div>
    </Grow>
  )
}
